import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Users, Trophy, Calendar, Edit } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const teamDoc = await getDoc(doc(db, 'teams', teamId));
        if (teamDoc.exists()) {
          setTeam({ id: teamDoc.id, ...teamDoc.data() });
        } else {
          setError('Team not found');
        }
      } catch (err) {
        setError('Failed to load team details');
        console.error('Error fetching team:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, [teamId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading team details...</div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1C563D' }}>
            {error || 'Team not found'}
          </h2>
          <Link to="/teams" className="btn btn-primary">
            Back to Teams
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <Link
          to="/teams"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Teams
        </Link>
        
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4 overflow-hidden">
              {team.logo ? (
                <img
                  src={team.logo}
                  alt={`${team.name} logo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-green-100 flex items-center justify-center">
                  <Users size={32} style={{ color: '#1C563D' }} />
                </div>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
                {team.name}
              </h1>
              <p className="text-gray-600">
                Team details and player roster
              </p>
            </div>
          </div>
          <Link
            to={`/teams/${teamId}/edit`}
            className="btn btn-primary flex items-center gap-2"
          >
            <Edit size={20} />
            Edit Team
          </Link>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Stats */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <div className="card">
            <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
              Team Statistics
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Matches Played</span>
                <span className="font-bold text-lg">{team.matchesPlayed || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-gray-600">Wins</span>
                <span className="font-bold text-lg text-green-600">{team.wins || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="text-gray-600">Losses</span>
                <span className="font-bold text-lg text-red-600">{team.losses || 0}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-gray-600">Points</span>
                <span className="font-bold text-lg" style={{ color: '#1C563D' }}>
                  {team.points || 0}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Players */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold" style={{ color: '#1C563D' }}>
                Players ({team.players?.length || 0})
              </h2>
            </div>
            
            {team.players && team.players.length > 0 ? (
              <div className="space-y-3">
                {team.players.map((player, index) => (
                  <motion.div
                    key={player.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                        <Users size={20} style={{ color: '#1C563D' }} />
                      </div>
                      <div>
                        <div className="font-medium">{player.name}</div>
                        <div className="text-sm text-gray-600">
                          {player.selected ? 'Selected for matches' : 'Reserve player'}
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/players/${player.id}`}
                      className="text-sm"
                      style={{ color: '#1C563D' }}
                    >
                      View Profile →
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users size={48} className="mx-auto mb-2 opacity-50" />
                <p>No players assigned to this team yet</p>
                <Link
                  to="/players/create"
                  className="btn btn-primary mt-4"
                >
                  Add Players
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeamDetailPage;
